import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'

interface RedirectablePerson {
  id: number
  user?: number | null
}

/**
 * A person with a linked user account is viewed under `/patients/:userId`, so a
 * `/people/:personId/...` URL for such a person is normalized to the patient URL
 * (preserving the tab and query); a fetch error goes to `/members`.
 *
 * Redirects with `replace: true`, never a push: a push would leave the
 * intermediate `/people/:id` entry on the stack, and because this redirect
 * re-fires whenever that entry re-mounts, Back would immediately bounce the user
 * forward again — a redirect trap that breaks the Back button. `onError` reports a
 * missing person (kept out of the hook so it stays free of app-specific concerns).
 */
export const usePersonToPatientRedirect = (
  person: RedirectablePerson | undefined,
  personFetchError: boolean,
  onError: () => void,
) => {
  const navigate = useNavigate()
  const location = useLocation()
  // No dependency array: mirror the pre-existing behavior of running on every
  // render. It is self-limiting — once redirected off `/people/...` this hook's
  // host unmounts — and each replace to the same URL is a no-op.
  useEffect(() => {
    if (personFetchError) {
      onError()
      void navigate('/members', { replace: true })
      return
    }
    if (person?.user != null) {
      const url = location.pathname.startsWith(`/people/${person.id}`)
        ? `${location.pathname.replace(`/people/${person.id}`, `/patients/${person.user}`)}${location.search}`
        : `/patients/${person.user}`
      void navigate(url, { replace: true })
    }
  })
}
